/**
 * Hook principal pour la vérification des autorisations
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { authorizationService } from '../services/authorizationService';

export const useAuthorization = (action, modelType, modelId = null, context = {}) => {
  const [canPerform, setCanPerform] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false); // Prevent concurrent fetches

  const checkPermission = useCallback(async () => {
    if (!action || !modelType) {
      setLoading(false);
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);

      const result = await authorizationService.canPerformAction(
        action, modelType, modelId, context
      );
      setCanPerform(result);
    } catch (err) {
      console.error('Permission check failed:', err);
      setError(err);
      setCanPerform(false);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [action, modelType, modelId, JSON.stringify(context)]);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Fonction pour recharger les permissions
  const refresh = useCallback(() => {
    checkPermission();
  }, [checkPermission]);

  return { canPerform, loading, error, refresh };
};

/**
 * Hook pour vérifier plusieurs permissions en une fois
 */
export const useMultipleAuthorizations = (checks) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false); // Prevent concurrent fetches

  const checkPermissions = useCallback(async () => {
    if (!checks || checks.length === 0) {
      setLoading(false);
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);

      const results = await authorizationService.checkMultiplePermissions(checks);
      setPermissions(results);
    } catch (err) {
      console.error('Multiple permissions check failed:', err);
      setError(err);
      setPermissions({});
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [JSON.stringify(checks)]);  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  const refresh = useCallback(() => {
    checkPermissions();
  }, [checkPermissions]);

  return { permissions, loading, error, refresh };
};

/**
 * Hook pour les capacités générales de l'utilisateur
 */
export const useUserCapabilities = () => {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false); // Prevent concurrent fetches

  const loadCapabilities = useCallback(async () => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);

      const result = await authorizationService.getUserCapabilities();
      setCapabilities(result);
    } catch (err) {
      console.error('Failed to load user capabilities:', err);
      setError(err);
      setCapabilities(null);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadCapabilities();
  }, [loadCapabilities]);

  const refresh = useCallback(() => {
    loadCapabilities();
  }, [loadCapabilities]);

  return { capabilities, loading, error, refresh };
};
